import { IRect } from "../../interfaces/IRect";
import { BaseRenderEngine } from "./BaseRenderEngine";
import { EditorData } from "../../data/EditorData";
import { EditorModel } from "../../staticModels/EditorModel";
import { ViewPortActions } from "../actions/ViewPortActions";
import { DrawUtil } from "../../utils/DrawUtil";
import { RenderEngineUtil } from "../../utils/RenderEngineUtil";
import { RenderEngineSettings } from "../../settings/RenderEngineSettings";
import { IPoint } from "../../interfaces/IPoint";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { ProjectType } from "../../data/enums/ProjectType";
import { PopupWindowType } from "../../data/enums/PopupWindowType";


export class PrimaryEditorRenderEngine extends BaseRenderEngine {
  // Cache of the filtered/gamma-corrected image so repeated renders (e.g. one
  // per mousemove while dragging a box prompt) can blit a pre-computed bitmap
  // instead of re-running ctx.filter + a per-pixel gamma loop every frame.
  private filteredImageCache: { key: string; canvas: HTMLCanvasElement } | null = null;
  private gammaLutCache: { gamma: number; lut: Uint8ClampedArray } | null = null;

  public constructor(canvas: HTMLCanvasElement) {
    super(canvas);
  }

  // =================================================================================================================
  // EVENT HANDLERS
  // =================================================================================================================

  public mouseMoveHandler(data: EditorData): void { }
  public mouseDownHandler(data: EditorData): void { }
  public mouseUpHandler(data: EditorData): void { }
  public pasteHandler(): void { }
  // =================================================================================================================
  // RENDERING
  // =================================================================================================================

  public render(data: EditorData): void {
    this.drawImage(
      EditorModel.image,
      ViewPortActions.calculateViewPortContentImageRect()
    );
    this.renderCrossHair(data);
  }

  public renderCrossHair(data: EditorData): void {
    if (!this.shouldRenderCrossHair(data)) return;

    const mouse = RenderEngineUtil.setPointBetweenPixels(
      data.mousePositionOnViewPortContent
    );
    const drawLine = (startPoint: IPoint, endPoint: IPoint) => {
      DrawUtil.drawLine(
        this.canvas,
        startPoint,
        endPoint,
        RenderEngineSettings.CROSS_HAIR_LINE_COLOR,
        0.8
      );
    };
    drawLine(
      { x: mouse.x, y: 0 },
      { x: mouse.x - 1, y: mouse.y - RenderEngineSettings.crossHairPadding }
    );
    drawLine(
      { x: mouse.x, y: mouse.y + RenderEngineSettings.crossHairPadding },
      { x: mouse.x - 1, y: data.viewPortContentSize.height }
    );
    drawLine(
      { x: 0, y: mouse.y },
      { x: mouse.x - RenderEngineSettings.crossHairPadding, y: mouse.y - 1 }
    );
    drawLine(
      { x: mouse.x + RenderEngineSettings.crossHairPadding, y: mouse.y },
      { x: data.viewPortContentSize.width, y: mouse.y - 1 }
    );
  }

  public shouldRenderCrossHair(data: EditorData): boolean {
    const isCrossHairVisible = GeneralSelector.getCrossHairVisibleStatus();
    const isImageInDragMode = GeneralSelector.getImageDragModeStatus();
    const isAiMode = GeneralSelector.getAiModeStatus();
    const projectType: ProjectType = GeneralSelector.getProjectType();
    const activePopupType: PopupWindowType =
      GeneralSelector.getActivePopupType();
    const isMouseOverCanvas: boolean = RenderEngineUtil.isMouseOverCanvas(data);
    const isCustomCursorBlocked =
      GeneralSelector.getPreventCustomCursorStatus();

    return [
      !!this.canvas,
      isCrossHairVisible,
      (isImageInDragMode || !isImageInDragMode),
      (isAiMode || !isAiMode),
      projectType !== ProjectType.IMAGE_RECOGNITION,
      !activePopupType,
      isMouseOverCanvas,
      !isCustomCursorBlocked,
    ].every(Boolean);
  }

  // public drawImage(image: HTMLImageElement, imageRect: IRect) {
  //   if (image && this.canvas) {
  //     const ctx = this.canvas.getContext("2d");
  //     const isGray = localStorage.getItem("grey-scale") === "true";
  //       const brightness = parseFloat(localStorage.getItem("brightness") || "1");
  //       const contrast = parseFloat(localStorage.getItem("contrast") || "1");
  //       ctx.filter = `
  //       ${isGray ? "grayscale(1)" : ""}
  //       brightness(${brightness})
  //        contrast(${contrast})
  //     `.trim();
  //     ctx.drawImage(
  //       image,
  //       imageRect.x,
  //       imageRect.y,
  //       imageRect.width,
  //       imageRect.height
  //     );

  //     ctx.filter = "none";


  //     // if (localStorage.getItem("grey-scale") == "true")
  //     //   ctx.filter = "grayscale(0)";

  //     //   var imageData = ctx.getImageData(
  //     //     0,
  //     //     0,
  //     //     imageRect.width+50,
  //     //     imageRect.height+50
  //     //   );
  //     // if(localStorage.getItem("grey-scale")=="true"){
  //     //     for (var i = 0; i < imageData.data.length; i += 4) {
  //     //         var avg =
  //     //           (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) /
  //     //           3;

  //     //         imageData.data[i] = avg;
  //     //         imageData.data[i + 1] = avg;
  //     //         imageData.data[i + 2] = avg;
  //     //       }

  //     //       ctx.putImageData(
  //     //         imageData,
  //     //         0,
  //     //         0,
  //     //         0,
  //     //         0,
  //     //         imageData.width+50,
  //     //         imageData.height+50
  //     //       );
  //     // }
  //   }
  // }

  private getGammaLut(gamma: number): Uint8ClampedArray {
    if (this.gammaLutCache && this.gammaLutCache.gamma === gamma) {
      return this.gammaLutCache.lut;
    }
    const lut = new Uint8ClampedArray(256);
    const invGamma = 1 / gamma;
    for (let i = 0; i < 256; i++) {
      lut[i] = 255 * Math.pow(i / 255, invGamma);
    }
    this.gammaLutCache = { gamma, lut };
    return lut;
  }

  public drawImage(image: HTMLImageElement, imageRect: IRect) {
    if (image && this.canvas) {
      const ctx = this.canvas.getContext("2d");

      const grayscale = GeneralSelector.getGrayscale();
      const brightness = GeneralSelector.getBrightness();
      const contrast = GeneralSelector.getContrast();
      const gamma = GeneralSelector.getGamma();

      const width = Math.max(1, Math.round(imageRect.width));
      const height = Math.max(1, Math.round(imageRect.height));
      const cacheKey = [
        image.src, grayscale, brightness, contrast, gamma, width, height,
      ].join("|");

      // Rebuild the filtered bitmap only when the image or filter settings
      // actually change; otherwise reuse the one already computed. This turns
      // the per-frame cost (ctx.filter + getImageData/putImageData + a
      // per-pixel gamma loop) into a single cheap drawImage blit for every
      // render that doesn't change what's being displayed (e.g. every
      // mousemove while dragging a box prompt).
      if (!this.filteredImageCache || this.filteredImageCache.key !== cacheKey) {
        const offscreen = this.filteredImageCache?.canvas ?? document.createElement("canvas");
        offscreen.width = width;
        offscreen.height = height;
        const offCtx = offscreen.getContext("2d");

        offCtx.filter = `
         grayscale(${grayscale})
          brightness(${brightness})
          contrast(${contrast})
        `.trim();
        offCtx.clearRect(0, 0, width, height);
        offCtx.drawImage(image, 0, 0, width, height);
        offCtx.filter = "none";

        if (gamma !== 1) {
          const imageData = offCtx.getImageData(0, 0, width, height);
          const data = imageData.data;
          const lut = this.getGammaLut(gamma);
          for (let i = 0; i < data.length; i += 4) {
            data[i] = lut[data[i]];         // Red
            data[i + 1] = lut[data[i + 1]]; // Green
            data[i + 2] = lut[data[i + 2]]; // Blue
          }
          offCtx.putImageData(imageData, 0, 0);
        }

        this.filteredImageCache = { key: cacheKey, canvas: offscreen };
      }

      ctx.drawImage(this.filteredImageCache.canvas, imageRect.x, imageRect.y, width, height);
    }
  }


  isInProgress(): boolean {
    return false;
  }
}
